import { Container, Grid, Paper, Typography, Box, Card, CardContent } from "@mui/material";
import { useState, useMemo, useEffect } from "react";

import { IPDCWelcomeMessage } from "../../../components"; //IPDCMessageCard
import {
  ParksBySectorCard,
  FinanceSummaryCard,
  InvestorActivityReportCard,
  ReferredResponseCard,
} from "../Shared/DashboardCards";
import ViewMessageList from "../Shared/Message/ViewMessageList";
import { GetDataFromApiWithParams } from "../../../services/api/ExecuteApiRequests";
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as CapitalIcon,
  Percent as PercentIcon,
  LocationOn as LocationOnIcon,
  Assignment as AssignmentIcon,
  Home as HomeIcon
} from "@mui/icons-material";

// Helper functions from code 2
const safeParseNumber = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/,/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

const formatPercent = (value) => {
  if (!value && value !== 0) return "0%";
  return `${Math.round(value)}%`;
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

// Gauge Chart for Conversion Rate
const ConversionGaugeChart = ({ conversionRate, potentialCount, investorCount }) => {
  const percentage = Math.min(100, Math.max(0, conversionRate));
  const angle = (percentage / 100) * 180;
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  
  const getColor = () => {
    if (percentage >= 70) return '#4caf50';
    if (percentage >= 40) return '#ff9800';
    return '#f44336';
  };
  
  return (
    <Box sx={{ bgcolor: '#F7F1F1', textAlign: 'center', p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2 }}>
        Investor Conversion Rate
      </Typography>
      
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <svg width={220} height={140} viewBox="0 0 220 140">
          {/* Background arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="15"
            strokeLinecap="round"
          />
          
          {/* Colored arc based on conversion rate */}
          <path
            d={`M 20 100 A 80 80 0 ${angle > 180 ? 1 : 0} 1 ${centerX + radius * Math.cos(Math.PI + (angle * Math.PI) / 180)} ${centerY + radius * Math.sin(Math.PI + (angle * Math.PI) / 180)}`}
            fill="none"
            stroke={getColor()}
            strokeWidth="15"
            strokeLinecap="round"
          />
          
          {/* Needle */}
          <line
            x1={centerX}
            y1={centerY - 10}
            x2={centerX + (radius - 20) * Math.cos(Math.PI + (percentage / 100) * Math.PI)}
            y2={centerY - 10 + (radius - 20) * Math.sin(Math.PI + (percentage / 100) * Math.PI)}
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx={centerX} cy={centerY - 10} r="6" fill="#333" />
          
          <text x={centerX} y={centerY + 25} textAnchor="middle" fontSize="24" fontWeight="bold" fill={getColor()}>
            {formatPercent(percentage)}
          </text>
        </svg>
      </Box>
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">Potential Investors</Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>{formatNumber(potentialCount)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">→ Converted →</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">Registered Investors</Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>{formatNumber(investorCount)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Horizontal Bar Chart Component for Top Sectors - MINIMAL GAPS
const HorizontalBarChart = ({ data, color, formatValue }) => {
  const items = Object.keys(data);
  const maxValue = Math.max(...Object.values(data), 0);
  
  if (items.length === 0 || maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 0.5 }}>
      {items.map((item, index) => {
        const value = data[item];
        const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
        const barWidth = percentage;
        
        return (
          <Box key={item} sx={{ mb: 0.15 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.7rem' }}>
                {item.length > 25 ? item.substring(0, 22) + '...' : item}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: color, fontSize: '0.65rem' }}>
                {formatValue(value)} ({percentage}%)
              </Typography>
            </Box>
            <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 16, overflow: 'hidden', position: 'relative' }}>
              <Box 
                sx={{ 
                  width: `${barWidth}%`, 
                  bgcolor: color, 
                  height: '100%',
                  borderRadius: 1,
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 0.3
                }} 
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  position: 'absolute', 
                  left: 4,
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: barWidth > 30 ? 'white' : '#333',
                  fontWeight: 'bold',
                  fontSize: '0.5rem'
                }}
              >
                {item.length > 20 ? item.substring(0, 17) + '...' : item}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Vertical Bar Chart Component for Top Investor Nationalities - MINIMAL GAPS
const VerticalBarChart = ({ data, color, formatValue }) => {
  const items = Object.keys(data);
  const maxValue = Math.max(...Object.values(data), 0);
  
  if (items.length === 0 || maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  // Calculate bar width based on number of items (histogram style)
  const barWidth = `${100 / items.length}%`;
  
  return (
    <Box sx={{ p: 0.5, overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', minWidth: '280px', height: '100%' }}>
        {items.map((item, index) => {
          const value = data[item];
          const percentage = maxValue > 0 ? ((value / maxValue) * 100) : 0;
          const barHeight = percentage;
          
          return (
            <Box 
              key={item} 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                width: barWidth,
                flexShrink: 0
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: color, 
                  mb: 0.15,
                  fontSize: '0.55rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatValue(value)}
              </Typography>
              <Box 
                sx={{ 
                  width: '100%', 
                  bgcolor: '#e0e0e0', 
                  height: 130,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    height: `${barHeight}%`, 
                    bgcolor: color, 
                    width: '100%',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    transition: 'height 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} 
                >
                  
                </Box>
              </Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  mt: 0.35, 
                  textAlign: 'center',
                  fontSize: '0.55rem',
                  fontWeight: 'medium',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.length > 12 ? item.substring(0, 10) + '...' : item}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Pie Chart Component for Top Parks
const PieChart = ({ data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  // Reduced size: smaller center, radius, and SVG size
  const centerX = 80, centerY = 80, radius = 60;
  const svgSize = 180;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      {/* Chart and legend in the same row - reduced gap and margin */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-start', ml: -1 }}>
        {/* Reduced size pie chart */}
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize, flexShrink: 0 }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="1.5"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            {/* Smaller inner circle */}
            <circle cx={centerX} cy={centerY} r="18" fill="white" />
            <text x={centerX} y={centerY - 3} textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 8} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        {/* Compact legend - adjusted margins */}
        <Box sx={{ flex: 1, maxHeight: 180, overflowY: 'auto', minWidth: 180, ml: -1 }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Box sx={{ width: 10, height: 10, bgcolor: colors[index % colors.length], borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ flex: 1, fontSize: '0.7rem', lineHeight: 1.2 }}>
                  {key.length > 25 ? key.substring(0, 22) + '...' : key}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length], fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                  {formatValue(value)} ({percentage}%)
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
    
    </Box>
  );
};

export default function PromotionAndMarketingDashboard() {
  const [investors, setInvestors] = useState([]);
  const [potentialInvestors, setPotentialInvestors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [mockInvestors, mockPotentialInvestors] = await Promise.all([
          GetDataFromApiWithParams(`investors/investorinfo`),
          GetDataFromApiWithParams(`prospectiveInvestors`),
        ]);
        
        setInvestors(mockInvestors || []);
        setPotentialInvestors(mockPotentialInvestors || []);
      } catch (error) {
        console.error("Failed to fetch investor data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalInvestors = investors.length;
    const totalPotential = potentialInvestors.length;
    const totalActiveInvestors = investors.filter(i => i.status === "ACTIVE" || i.status === null).length;
    const totalCapital = investors.reduce((sum, i) => sum + safeParseNumber(i.capital), 0);
    
    // Calculate conversion rate
    const convertedCount = potentialInvestors.filter(potential => 
      investors.some(investor => 
        investor.email?.toLowerCase() === potential.email?.toLowerCase() ||
        investor.company_name?.toLowerCase() === potential.investor_company_name?.toLowerCase()
      )
    ).length;
    
    const conversionRate = totalPotential > 0 ? (convertedCount / totalPotential) * 100 : 0;
    
    // Sector distribution from potential investors
    const sectorDistribution = {};
    potentialInvestors.forEach(pot => {
      const sector = pot.sector_of_interest || "Unknown";
      sectorDistribution[sector] = (sectorDistribution[sector] || 0) + 1;
    });
    
    // Sort sector distribution and get top 5
    const sortedSectors = Object.entries(sectorDistribution)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const topSectors = Object.fromEntries(sortedSectors);
    
    // Park interest distribution
    const parkDistribution = {};
    potentialInvestors.forEach(pot => {
      const park = pot.park_interested_in || "Unknown";
      parkDistribution[park] = (parkDistribution[park] || 0) + 1;
    });
    
    // Sort park distribution and get top 5
    const sortedParks = Object.entries(parkDistribution)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const topParks = Object.fromEntries(sortedParks);
    
    // Country distribution from investors
    const countryDistribution = {};
    investors.forEach(inv => {
      let country = inv.nationality_origin || "Unknown";
    
      // normalize
      country = country.trim().toLowerCase();
    
      // fix known spelling variations
      const aliases = {
        ethiopa: "ethiopia",
        "china /honghong/": "china",
        hongkong: "china",
        "taiwan (china)": "china",
        "sirilanka": "sri lanka",
        "sirilanka/china": "sri lanka",
      };
    
      country = aliases[country] || country;
    
      // count
      if (countryDistribution[country]) {
        countryDistribution[country]++;
      } else {
        countryDistribution[country] = 1;
      }
    });
    
    // Sort country distribution and get top 5
    const sortedCountries = Object.entries(countryDistribution)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const topCountries = Object.fromEntries(sortedCountries);
    
    return {
      totalInvestors,
      totalPotential,
      totalActiveInvestors,
      totalCapital,
      conversionRate,
      convertedCount,
      topSectors,
      topParks,
      topCountries
    };
  }, [investors, potentialInvestors]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: '#f4f9ff', width: '98%', mx: '1%', py: 1 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Typography>Loading dashboard data...</Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f4f9ff', width: '98%', mx: '1%', py: 1 }}>
      {/* Header */}
      <Box sx={{ py: 2, px: 3 }}>
        <Container maxWidth="xl">
          <Grid item xs={12} md={12}>
            <IPDCWelcomeMessage messageTitle="Promotion & Marketing" />
          </Grid>
        </Container>
      </Box>

      {/* Summary Statistics Cards */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd', boxShadow: 1, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <BusinessIcon sx={{ color: '#1976d2', fontSize: 28, mb: 1 }} />
                <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                  {formatNumber(summaryStats.totalInvestors)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Total Investors</Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd', boxShadow: 1, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <TrendingUpIcon sx={{ color: '#ff9800', fontSize: 28, mb: 1 }} />
                <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                  {formatNumber(summaryStats.totalPotential)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Potential Investors</Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd', boxShadow: 1, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28, mb: 1 }} />
                <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                  {formatNumber(summaryStats.totalActiveInvestors)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Active Investors</Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd', boxShadow: 1, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <CapitalIcon sx={{ color: '#9c27b0', fontSize: 28, mb: 1 }} />
                <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                  {formatCurrency(summaryStats.totalCapital)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Total Capital</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Conversion Rate and Parks by Sector */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#F7F1F1', height: '100%' }}>
              <ConversionGaugeChart 
                conversionRate={summaryStats.conversionRate}
                potentialCount={summaryStats.totalPotential}
                investorCount={summaryStats.convertedCount}
              />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ParksBySectorCard wrapLegendText={true} />
          </Grid>
        </Grid>
      </Container>

      {/* Top Sectors (Horizontal Bar) and Top Parks (Pie Chart) */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Top Sectors of Interest - Horizontal Bar Chart */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Top Sectors of Interest
                </Typography>
              </Box>
              <HorizontalBarChart 
                data={summaryStats.topSectors}
                color="#ff9800"
                formatValue={formatNumber}
              />
              <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Sectors: {Object.keys(summaryStats.topSectors).length}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          {/* Top Parks Interest - Pie Chart */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Top Parks Interest
                </Typography>
              </Box>
              <PieChart 
                data={summaryStats.topParks}
                formatValue={formatNumber}
              />
            </Paper>
          </Grid>

          {/* Top Investor Nationalities - Vertical Bar Chart */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HomeIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Top Investor Nationalities
                </Typography>
              </Box>
              <VerticalBarChart 
                data={summaryStats.topCountries}
                color="#2196f3"
                formatValue={formatNumber}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}